import React from "react";
import { assumptionRegistry, sourceRegistryV2 } from "../data/snapshots.js";
import { generatedEvidenceGapPriorityLedger } from "../data/generatedEvidenceGapPriorityLedger.js";
import { generatedFeasibilityReport } from "../data/generatedFeasibilityReport.js";
import { generatedModelArtifactBundle } from "../data/generatedModelArtifacts.js";
import { generatedModelValidationProtocolLedger } from "../data/generatedModelValidationProtocols.js";
import { generatedRuntimeValidationCampaignLedger } from "../data/generatedRuntimeValidationCampaignLedger.js";
import { generatedScenarioDecisionAuditLedger } from "../data/generatedScenarioDecisionAuditLedger.js";
import { generatedScenarioNotebookPack } from "../data/generatedScenarioNotebookPack.js";
import { generatedScenarioTraceabilityMatrix } from "../data/generatedScenarioTraceabilityMatrix.js";
import { generatedSidecarArtifactTemplatePacket } from "../data/generatedSidecarArtifactTemplates.js";
import { generatedSidecarPromotionLedger } from "../data/generatedSidecarPromotionLedger.js";
import { generatedSidecarReadinessLedger } from "../data/generatedSidecarReadinessLedger.js";
import { generatedSidecarRunSpecPacket } from "../data/generatedSidecarRunSpecs.js";
import { generatedSidecarValidationReport } from "../data/generatedSidecarValidationReport.js";
import { generatedSidecarWorkOrderPacket } from "../data/generatedSidecarWorkOrders.js";
import { generatedSourceIngestionLedger } from "../data/generatedSourceIngestionLedger.js";
import { generatedSourcePromotionLedger } from "../data/generatedSourcePromotionLedger.js";
import { generatedSourceQueryCustodyLedger } from "../data/generatedSourceQueryCustodyLedger.js";
import { generatedSourceRefreshReport } from "../data/generatedSourceRefreshReport.js";
import { generatedSourceUseClearanceDossier } from "../data/generatedSourceUseClearanceDossier.js";
import { generatedSourceUseReviewPacket } from "../data/generatedSourceUseReviewPackets.js";
import { generatedSourceUseRightsLedger } from "../data/generatedSourceUseRightsLedger.js";
import { round, sourceQualityScore } from "../science/kernel.mjs";
import {
  CellStack,
  DataTable,
  EvidencePanel,
  Metric,
  PageHeader,
  ScoreBarCell,
  StackList,
  StatusPill,
} from "./ui.jsx";

export function SourceLibraryEvidencePanels() {
  const registryRows = sourceRegistryV2.map((record) => ({
    ...record,
    score: sourceQualityScore({
      authority: record.trust === "primary" ? 0.95 : 0.72,
      reproducibility: record.queryPolicy ? 0.86 : 0.55,
      licenseClarity: record.license ? 0.72 : 0.35,
      uncertaintyDisclosure: record.uncertaintyMode ? 0.78 : 0.4,
      freshness: record.capturedAt === "2026-06-19" ? 0.82 : 0.5,
      independence: record.trust === "primary" ? 0.72 : 0.58,
    }),
  }));
  const artifactRows = generatedModelArtifactBundle.generatedArtifacts.map((artifact) => ({
    ...artifact,
    readiness: artifact.readinessScore,
  }));
  const blockedArtifacts = artifactRows.filter((artifact) =>
    !artifact.promotionAllowed ||
    /blocked|contract|fixture|design/.test(`${artifact.claimState} ${artifact.runtimeStatus}`)
  ).length;

  return (
    <section className="module">
      <PageHeader
        title="SOURCE LIBRARY"
        muted="Evidence and model provenance"
        subtitle="Every source, assumption, and model output must be reproducible before it can influence business claims."
      />
      <div className="metric-grid-4">
        <Metric label="Source records" value={sourceRegistryV2.length} />
        <Metric label="Generated artifacts" value={generatedModelArtifactBundle.artifactCount} />
        <Metric label="Assumptions" value={assumptionRegistry.length} />
        <Metric label="Blocked artifacts" value={blockedArtifacts} />
        <Metric label="Ingestion orders" value={generatedSourceIngestionLedger.workOrderCount} />
        <Metric label="Blocked ingestion" value={generatedSourceIngestionLedger.blockedWorkOrderCount} />
        <Metric label="Query custody" value={generatedSourceQueryCustodyLedger.queryCount} />
        <Metric label="Promoted sources" value={generatedSourcePromotionLedger.promotedCount} />
        <Metric label="P0 source gaps" value={generatedSourcePromotionLedger.p0PromotionGapCount} />
        <Metric label="Review packets" value={generatedSourceUseReviewPacket.packetCount} />
        <Metric label="Use-rights blocked" value={generatedSourceUseRightsLedger.blockedSourceUseCount} />
        <Metric label="High-risk sources" value={generatedSourceUseRightsLedger.riskCounts.high ?? 0} />
      </div>
      <EvidencePanel title="Artifact bundle" status={generatedModelArtifactBundle.mode}>
        <div className="metric-grid-4">
          <Metric label="Generated at" value={generatedModelArtifactBundle.generatedAt.slice(0, 10)} />
          <Metric label="Bundle hash" value={generatedModelArtifactBundle.bundleHash.slice(0, 18)} />
          <Metric label="Source refresh" value={generatedSourceRefreshReport.reportHash.slice(0, 18)} />
          <Metric label="Report hash" value={generatedFeasibilityReport.reportHash} />
        </div>
        <p>{generatedModelArtifactBundle.promotionRule}</p>
        <p>Canonical report: {generatedFeasibilityReport.executiveSummary.selectedScenarioName}; readiness blockers {generatedFeasibilityReport.readiness.blockers.length}. Source refresh mode: {generatedSourceRefreshReport.mode}. Query custody: {generatedSourceQueryCustodyLedger.queryCount} contracts, {generatedSourceQueryCustodyLedger.liveFetchCount} live fetches. Sidecar gate: {generatedSidecarValidationReport.promotedCount}/{generatedSidecarValidationReport.validationCount} promoted. Runtime sidecar promotions: {generatedSidecarPromotionLedger.promotedCount}.</p>
      </EvidencePanel>
      <EvidencePanel title="Source ingestion queue" status={generatedSourceIngestionLedger.mode}>
        <div className="metric-grid-4">
          <Metric label="Sources covered" value={generatedSourceIngestionLedger.sourceCount} />
          <Metric label="Work orders" value={generatedSourceIngestionLedger.workOrderCount} />
          <Metric label="Blocked until payload" value={generatedSourceIngestionLedger.blockedWorkOrderCount} />
          <Metric label="Ledger hash" value={generatedSourceIngestionLedger.ledgerHash.slice(0, 18)} />
        </div>
        <p>{generatedSourceIngestionLedger.operatingRule}</p>
        <StackList
          items={generatedSourceIngestionLedger.priorityQueue.slice(0, 8)}
          getTitle={(order) => order.title}
          getMeta={(order) => `${order.priority} / ${order.domain}`}
          getBody={(order) => order.nextAction}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Source promotion ledger"
        status={generatedSourcePromotionLedger.promotionState}
        tone={generatedSourcePromotionLedger.promotionAllowed ? "green" : "red"}
      >
        <div className="metric-grid-4">
          <Metric label="Reports" value={generatedSourcePromotionLedger.reportCount} />
          <Metric label="Valid reports" value={generatedSourcePromotionLedger.validReportCount} />
          <Metric label="Promoted payloads" value={generatedSourcePromotionLedger.promotedCount} />
          <Metric label="Ledger hash" value={generatedSourcePromotionLedger.ledgerHash.slice(0, 18)} />
        </div>
        <p>{generatedSourcePromotionLedger.operatingRule}</p>
        <StackList
          items={generatedSourcePromotionLedger.p0PromotionGaps.slice(0, 6)}
          getKey={(gap) => gap.workOrderId}
          getTitle={(gap) => gap.title}
          getMeta={(gap) => `${gap.sourceId} / ${gap.domain}`}
          getBody={(gap) => gap.nextAction}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Source use-rights ledger"
        status={generatedSourceUseRightsLedger.customerUseAllowed ? "cleared" : "blocked"}
        tone={generatedSourceUseRightsLedger.customerUseAllowed ? "green" : "red"}
      >
        <div className="metric-grid-4">
          <Metric label="Sources reviewed" value={generatedSourceUseRightsLedger.sourceCount} />
          <Metric label="Blocked for customer use" value={generatedSourceUseRightsLedger.blockedSourceUseCount} />
          <Metric label="Raw redistribution" value={generatedSourceUseRightsLedger.rawRedistributionAllowed ? "yes" : "no"} />
          <Metric label="Use policies" value={generatedSourceUseRightsLedger.usePolicyMatrix.length} />
          <Metric label="Ledger hash" value={generatedSourceUseRightsLedger.ledgerHash.slice(0, 18)} />
        </div>
        <p>{generatedSourceUseRightsLedger.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedSourceUseRightsLedger.usePolicyMatrix}
          getRowKey={(policy) => policy.sourceUseClass}
          columns={[
            {
              key: "sourceUseClass",
              header: "Source class",
              render: (policy) => (
                <CellStack
                  title={policy.sourceUseClass}
                  meta={`${policy.sourceCount} sources / ${policy.riskLevels.join(", ")}`}
                />
              ),
            },
            {
              key: "internalResearch",
              header: "Internal use",
              render: (policy) => policy.customerReportUse.internalResearch,
            },
            {
              key: "derivedValues",
              header: "Derived values",
              render: (policy) => policy.customerReportUse.derivedNormalizedValues,
            },
            {
              key: "rawPayloads",
              header: "Raw payloads",
              render: (policy) => policy.customerReportUse.rawPayloadRedistribution,
            },
            {
              key: "providerMarks",
              header: "Provider marks",
              render: (policy) => policy.customerReportUse.providerMarksOrEndorsement,
            },
            { key: "defaultBoundary", header: "Boundary" },
          ]}
        />
        <StackList
          items={generatedSourceUseRightsLedger.topReviewQueue.slice(0, 6)}
          getKey={(source) => source.sourceId}
          getTitle={(source) => source.title}
          getMeta={(source) => `${source.sourceUseClass} / ${source.riskLevel}`}
          getBody={(source) => source.nextAction}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Source use review packets"
        status={generatedSourceUseReviewPacket.customerUseAllowed ? "cleared" : "blocked"}
        tone={generatedSourceUseReviewPacket.customerUseAllowed ? "green" : "red"}
      >
        <div className="metric-grid-4">
          <Metric label="Packets" value={generatedSourceUseReviewPacket.packetCount} />
          <Metric label="Blocked packets" value={generatedSourceUseReviewPacket.blockedPacketCount} />
          <Metric label="P0 queue" value={generatedSourceUseReviewPacket.priorityCounts.P0 ?? 0} />
          <Metric label="Packet hash" value={generatedSourceUseReviewPacket.packetHash.slice(0, 18)} />
        </div>
        <p>{generatedSourceUseReviewPacket.operatingRule}</p>
        <StackList
          items={generatedSourceUseReviewPacket.reviewQueue.slice(0, 6)}
          getTitle={(packet) => packet.title}
          getMeta={(packet) => `${packet.priority} / ${packet.sourceUseClass} / ${packet.riskLevel}`}
          getBody={(packet) => packet.nextAction}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Source use clearance dossier"
        status={generatedSourceUseClearanceDossier.mode}
        tone={generatedSourceUseClearanceDossier.customerUseAllowed ? "green" : "red"}
      >
        <div className="metric-grid-4">
          <Metric label="Classes" value={generatedSourceUseClearanceDossier.classCount} />
          <Metric label="Blocked sources" value={generatedSourceUseClearanceDossier.blockedSourceCount} />
          <Metric label="Raw redistribution" value={generatedSourceUseClearanceDossier.rawRedistributionAllowed ? "clear" : "blocked"} />
          <Metric label="Dossier hash" value={generatedSourceUseClearanceDossier.dossierHash.slice(0, 18)} />
        </div>
        <p>{generatedSourceUseClearanceDossier.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedSourceUseClearanceDossier.classDossiers}
          getRowKey={(record) => record.sourceUseClass}
          columns={[
            {
              key: "sourceUseClass",
              header: "Class",
              render: (record) => (
                <CellStack
                  title={record.sourceUseClass}
                  meta={`${record.sourceCount} sources / ${record.blockedPacketCount} packets`}
                />
              ),
            },
            { key: "priority", header: "Priority" },
            { key: "decisionState", header: "Decision" },
            { key: "blockedUntil", header: "Blocked until" },
            {
              key: "requiredDecisionIds",
              header: "Review decisions",
              render: (record) => record.requiredDecisionIds.slice(0, 3).join(", "),
            },
          ]}
        />
        <StackList
          items={generatedSourceUseClearanceDossier.classDossiers.slice(0, 3)}
          getKey={(record) => record.classDossierHash}
          getTitle={(record) => `${record.priority} ${record.sourceUseClass}`}
          getMeta={() => "customer use blocked / raw redistribution blocked"}
          getBody={(record) => record.nextAction}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Scenario traceability matrix"
        status={generatedScenarioTraceabilityMatrix.mode}
        tone={generatedScenarioTraceabilityMatrix.promotionAllowed ? "green" : "red"}
      >
        <div className="metric-grid-4">
          <Metric label="Scenario traces" value={generatedScenarioTraceabilityMatrix.scenarioCount} />
          <Metric label="Blocked traces" value={generatedScenarioTraceabilityMatrix.blockedTraceCount} />
          <Metric label="Matrix hash" value={generatedScenarioTraceabilityMatrix.matrixHash.slice(0, 18)} />
          <Metric label="Decision hash" value={generatedScenarioTraceabilityMatrix.evidenceHashes.scenarioDecisionLedger.slice(0, 18)} />
        </div>
        <p>{generatedScenarioTraceabilityMatrix.operatingRule}</p>
        <StackList
          items={generatedScenarioTraceabilityMatrix.traces.slice(0, 6)}
          getKey={(trace) => trace.scenarioId}
          getTitle={(trace) => trace.name}
          getMeta={(trace) => `${trace.decisionLane} / ${trace.blockerCount} blockers`}
          getBody={(trace) => trace.allowedUse}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Scenario decision audits"
        status={generatedScenarioDecisionAuditLedger.mode}
        tone={generatedScenarioDecisionAuditLedger.commercialUseAllowed ? "green" : "amber"}
      >
        <div className="metric-grid-4">
          <Metric label="Audits" value={generatedScenarioDecisionAuditLedger.auditCount} />
          <Metric label="Leader" value={generatedScenarioDecisionAuditLedger.leaderScenarioId} />
          <Metric label="Commercial use" value={generatedScenarioDecisionAuditLedger.commercialUseAllowed ? "yes" : "no"} />
          <Metric label="Ledger hash" value={generatedScenarioDecisionAuditLedger.ledgerHash.slice(0, 18)} />
        </div>
        <p>{generatedScenarioDecisionAuditLedger.operatingRule}</p>
        <StackList
          items={generatedScenarioDecisionAuditLedger.topCrossoverQuestions}
          getKey={(question) => question.scenarioId}
          getTitle={(question) => question.scenarioId}
          getMeta={(question) => `${question.decisionLane} / delta ${question.requiredDelta}`}
          getBody={(question) => `${question.flipKind}; blockers ${question.blockerCount}`}
        />
      </EvidencePanel>
      <EvidencePanel title="Evidence gap priority ledger" status={generatedEvidenceGapPriorityLedger.mode}>
        <div className="metric-grid-4">
          <Metric label="Evidence gaps" value={generatedEvidenceGapPriorityLedger.gapCount} />
          <Metric label="P0 gaps" value={generatedEvidenceGapPriorityLedger.p0GapCount} />
          <Metric label="Experiment backlog" value={generatedEvidenceGapPriorityLedger.experimentBacklogCount} />
          <Metric label="Ledger hash" value={generatedEvidenceGapPriorityLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Runtime gaps" value={generatedEvidenceGapPriorityLedger.categoryCounts["runtime-sidecar"] ?? 0} />
          <Metric label="Runtime experiments" value={generatedEvidenceGapPriorityLedger.experimentLaneCounts["runtime-validation"] ?? 0} />
        </div>
        <p>{generatedEvidenceGapPriorityLedger.operatingRule}</p>
        <StackList
          items={generatedEvidenceGapPriorityLedger.topGaps.slice(0, 6)}
          getTitle={(gap) => gap.title}
          getMeta={(gap) => `${gap.priorityLane} / score ${gap.priorityScore}`}
          getBody={(gap) => gap.nextAction}
        />
        <DataTable
          shell="shell"
          rows={generatedEvidenceGapPriorityLedger.experimentBacklog.slice(0, 6)}
          getRowKey={(experiment) => experiment.id}
          columns={[
            {
              key: "title",
              header: "Experiment",
              render: (experiment) => (
                <CellStack title={experiment.title} meta={experiment.reproducibility.planningCommand} />
              ),
            },
            { key: "lane", header: "Lane" },
            {
              key: "rankingChangeRequired",
              header: "What would change",
              render: (experiment) => experiment.decisionImpact.rankingChangeRequired,
            },
            {
              key: "affectedScenarioIds",
              header: "Affected scenarios",
              render: (experiment) => experiment.affectedScenarioIds.join(", "),
            },
            { key: "currentState", header: "State" },
            {
              key: "experimentHash",
              header: "Hash",
              render: (experiment) => <code>{experiment.experimentHash.slice(0, 20)}</code>,
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel
        title="Model validation protocols"
        status={generatedModelValidationProtocolLedger.mode}
        tone={generatedModelValidationProtocolLedger.commercialUseAllowed ? "green" : "amber"}
      >
        <div className="metric-grid-4">
          <Metric label="Protocols" value={generatedModelValidationProtocolLedger.protocolCount} />
          <Metric label="Blocked protocols" value={generatedModelValidationProtocolLedger.blockedProtocolCount} />
          <Metric label="Commercial use" value={generatedModelValidationProtocolLedger.commercialUseAllowed ? "yes" : "no"} />
          <Metric label="Ledger hash" value={generatedModelValidationProtocolLedger.ledgerHash.slice(0, 18)} />
        </div>
        <p>{generatedModelValidationProtocolLedger.operatingRule}</p>
        <StackList
          items={generatedModelValidationProtocolLedger.protocols.slice(0, 6)}
          getTitle={(protocol) => protocol.title}
          getMeta={(protocol) => `${protocol.validationState} / ${protocol.blockers.length} blockers`}
          getBody={(protocol) => protocol.acceptanceCriteria[0]}
        />
      </EvidencePanel>
      <EvidencePanel title="Sidecar execution packet" status={generatedSidecarWorkOrderPacket.mode}>
        <div className="metric-grid-4">
          <Metric label="Work orders" value={generatedSidecarWorkOrderPacket.workOrderCount} />
          <Metric label="Blocked until workers" value={generatedSidecarWorkOrderPacket.blockedWorkOrderCount} />
          <Metric label="Run specs" value={generatedSidecarRunSpecPacket.specCount} />
          <Metric label="Spec hash" value={generatedSidecarRunSpecPacket.packetHash.slice(0, 18)} />
          <Metric label="Artifact templates" value={generatedSidecarArtifactTemplatePacket.templateCount} />
          <Metric label="Template hash" value={generatedSidecarArtifactTemplatePacket.packetHash.slice(0, 18)} />
          <Metric label="Packet hash" value={generatedSidecarWorkOrderPacket.packetHash.slice(0, 18)} />
          <Metric label="Validation hash" value={generatedSidecarWorkOrderPacket.validationReportHash.slice(0, 18)} />
          <Metric label="Runtime reports" value={generatedSidecarPromotionLedger.reportCount} />
          <Metric label="Runtime promoted" value={generatedSidecarPromotionLedger.promotedCount} />
          <Metric label="Promoted contracts" value={generatedSidecarPromotionLedger.promotedContractCount} />
          <Metric label="Contract gaps" value={generatedSidecarPromotionLedger.contractPromotionGapCount} />
          <Metric label="Promotion hash" value={generatedSidecarPromotionLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Readiness contracts" value={generatedSidecarReadinessLedger.contractCount} />
          <Metric label="Ready to run" value={generatedSidecarReadinessLedger.readyToRunContractCount} />
          <Metric label="Runtime blocked" value={generatedSidecarReadinessLedger.blockedRuntimeContractCount} />
          <Metric label="Current reproducible" value={generatedSidecarReadinessLedger.currentReproduciblePromotedContractCount} />
          <Metric label="Repro debt" value={generatedSidecarReadinessLedger.promotedButNotCurrentlyReproducibleCount} />
          <Metric label="Env drift" value={generatedSidecarReadinessLedger.environmentDriftContractCount} />
          <Metric label="Readiness hash" value={generatedSidecarReadinessLedger.ledgerHash.slice(0, 18)} />
          <Metric label="Runtime campaigns" value={generatedRuntimeValidationCampaignLedger.campaignCount} />
          <Metric label="Campaign blockers" value={generatedRuntimeValidationCampaignLedger.blockedCampaignCount} />
          <Metric label="Campaign hash" value={generatedRuntimeValidationCampaignLedger.ledgerHash.slice(0, 18)} />
        </div>
        <p>{generatedSidecarRunSpecPacket.operatingRule}</p>
        <p>{generatedSidecarArtifactTemplatePacket.operatingRule}</p>
        <p>{generatedSidecarPromotionLedger.operatingRule}</p>
        <p>{generatedSidecarReadinessLedger.operatingRule}</p>
        <p><code>{generatedRuntimeValidationCampaignLedger.mode}</code></p>
        <p>{generatedRuntimeValidationCampaignLedger.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedSidecarPromotionLedger.validations.filter((validation) => validation.promotionAllowed)}
          getRowKey={(validation) => `${validation.reportPath}-${validation.artifactId}`}
          columns={[
            { key: "artifactId", header: "Promoted artifact" },
            { key: "contractId", header: "Contract" },
            { key: "scenarioId", header: "Scenario" },
            {
              key: "fieldCoveragePercent",
              header: "Coverage",
              render: (validation) => `${validation.fieldCoveragePercent}%`,
            },
            {
              key: "artifactHash",
              header: "Artifact hash",
              render: (validation) => validation.artifactHash?.slice(0, 24),
            },
          ]}
        />
        <DataTable
          shell="shell"
          rows={generatedSidecarReadinessLedger.contracts}
          getRowKey={(contract) => contract.contractId}
          columns={[
            { key: "tool", header: "Contract" },
            { key: "readinessState", header: "State" },
            { key: "reproducibilityState", header: "Reproducibility" },
            {
              key: "latestPreflight",
              header: "Latest preflight",
              render: (contract) => contract.latestPreflight?.preflightState ?? "no preflight",
            },
            {
              key: "bestActionablePreflight",
              header: "Best preflight",
              render: (contract) => contract.bestActionablePreflight?.preflightState ?? "no preflight",
            },
            {
              key: "blockedProbeIds",
              header: "Blocked probes",
              render: (contract) => contract.latestPreflight?.blockedProbeIds?.join(", ") || "none",
            },
            { key: "nextAction", header: "Next action" },
          ]}
        />
        <DataTable
          shell="shell"
          rows={generatedRuntimeValidationCampaignLedger.campaigns}
          getRowKey={(campaign) => campaign.id}
          columns={[
            {
              key: "title",
              header: "Runtime validation campaign",
              render: (campaign) => (
                <CellStack title={campaign.title} meta={<code>{campaign.campaignHash.slice(0, 20)}</code>} />
              ),
            },
            { key: "priorityLane", header: "Priority" },
            { key: "campaignStatus", header: "Status" },
            {
              key: "contractIds",
              header: "Contracts",
              render: (campaign) => campaign.contractIds.join(", "),
            },
            {
              key: "scenarioIds",
              header: "Scenarios",
              render: (campaign) => campaign.scenarioIds.join(", "),
            },
            {
              key: "blockers",
              header: "Blockers",
              render: (campaign) => campaign.blockers.length,
            },
            { key: "nextAction", header: "Next action" },
          ]}
        />
        <StackList
          items={[
            ...generatedSidecarRunSpecPacket.runSpecs.slice(0, 5).map((spec) => ({
              key: spec.id,
              title: spec.tool,
              meta: `${spec.workerRuntime} / ${spec.runState}`,
              body: spec.promotionBoundary,
            })),
            ...generatedSidecarArtifactTemplatePacket.templates.slice(0, 5).map((template) => ({
              key: template.id,
              title: `${template.tool} template`,
              meta: `${template.requiredFieldCount} fields / ${template.templateState}`,
              body: template.promotionBoundary,
            })),
            ...generatedSidecarPromotionLedger.contractPromotionGaps.map((gap) => ({
              key: gap.contractId,
              title: gap.tool,
              meta: `${gap.domain} / runtime artifact required`,
              body: gap.promotionRule,
            })),
          ]}
          getTitle={(item) => item.title}
          getMeta={(item) => item.meta}
          getBody={(item) => item.body}
        />
      </EvidencePanel>
      <EvidencePanel title="Source Registry 2.0" status="hash tracked" tone="green">
        <div className="source-grid">
          {registryRows.map((record) => (
            <a className="source-tile" href={record.url} key={record.sourceId} target="_blank" rel="noreferrer">
              <strong>{record.title}</strong>
              <span>{record.organization}</span>
              <p>{record.supports}</p>
              <div className="source-meta-row">
                <StatusPill tone={record.trust === "primary" ? "green" : "blue"}>{record.trust}</StatusPill>
                <StatusPill tone={record.score >= 80 ? "green" : "amber"}>{round(record.score, 0)}</StatusPill>
              </div>
              <small>{record.sourceType}</small>
              <code>{record.recordHash}</code>
            </a>
          ))}
        </div>
      </EvidencePanel>
      <EvidencePanel title="Reproducible model artifacts" status="sidecar backlog" className="panel-large">
        <DataTable
          rows={artifactRows}
          getRowKey={(artifact) => artifact.id}
          columns={[
            { key: "name", header: "Artifact" },
            { key: "domain", header: "Domain" },
            {
              key: "claimState",
              header: "State",
              render: (artifact) => (
                <StatusPill tone={artifact.claimState.includes("blocked") ? "red" : "blue"}>
                  {artifact.claimState}
                </StatusPill>
              ),
            },
            {
              key: "readiness",
              header: "Readiness",
              render: (artifact) => (
                <ScoreBarCell
                  value={artifact.readiness}
                  tone={artifact.readiness > 70 ? "green" : "amber"}
                  label={round(artifact.readiness, 0)}
                />
              ),
            },
            { key: "runtimeStatus", header: "Runtime" },
            {
              key: "generatedHash",
              header: "Hash",
              render: (artifact) => <code>{artifact.generatedHash.slice(0, 20)}</code>,
            },
            { key: "nextAction", header: "Next action" },
          ]}
        />
      </EvidencePanel>
      <div className="module-grid">
        <EvidencePanel title="Assumption registry" status={assumptionRegistry.length} tone="blue">
          <StackList
            items={assumptionRegistry}
            getTitle={(assumption) => assumption.title}
            getMeta={(assumption) =>
              `${assumption.owner} / ${assumption.state} / uncertainty ${assumption.uncertainty}`
            }
            getBody={(assumption) => assumption.testGate}
          />
        </EvidencePanel>
        <EvidencePanel title="Scenario notebook pack" status={generatedScenarioNotebookPack.mode} tone="blue">
          <div className="metric-grid-4 compact-metrics">
            <Metric label="Notebooks" value={generatedScenarioNotebookPack.notebookCount} />
            <Metric label="Source defined" value={generatedScenarioNotebookPack.sourceDefinedNotebookCount} />
            <Metric label="Generated gaps" value={generatedScenarioNotebookPack.generatedCoverageGapNotebookCount} />
            <Metric label="Customer use" value={generatedScenarioNotebookPack.customerUseAllowed ? "allowed" : "blocked"} />
          </div>
          <p className="panel-note">
            Every scenario has a hash-backed notebook and Markdown export. `generated-coverage-gap-notebook`
            entries are reviewer placeholders, not customer-ready science.
          </p>
          <DataTable
            shell="shell"
            rows={generatedScenarioNotebookPack.notebooks}
            getRowKey={(notebook) => notebook.id}
            columns={[
              {
                key: "title",
                header: "Notebook",
                render: (notebook) => (
                  <CellStack title={notebook.title} meta={<code>{notebook.notebookHash.slice(0, 20)}</code>} />
                ),
              },
              { key: "scenarioName", header: "Scenario" },
              {
                key: "notebookState",
                header: "State",
                render: (notebook) => (
                  <StatusPill tone={notebook.sourceNotebookCoverage === "source-defined" ? "green" : "amber"}>
                    {notebook.notebookState}
                  </StatusPill>
                ),
              },
              {
                key: "openQuestions",
                header: "Questions",
                render: (notebook) => notebook.openQuestions.length,
              },
              {
                key: "blockers",
                header: "Blockers",
                render: (notebook) => notebook.blockers.length,
              },
              {
                key: "markdownPath",
                header: "Markdown",
                render: (notebook) => <code>{notebook.markdownPath.replace("public/", "")}</code>,
              },
            ]}
          />
        </EvidencePanel>
      </div>
    </section>
  );
}
